/**
 * XRHapticsManager — haptic feedback for XR controllers.
 *
 * Provides pulse and pattern-based haptics via the GamepadHapticActuator API.
 * No-op when haptics are unavailable.
 */

import type { XRSessionState } from './types.js';

export interface HapticPattern {
  intensity: number; // 0..1
  duration: number;  // ms
  pause: number;     // ms pause after this step
}

const _noop = (): void => {};

export class XRHapticsManager {
  private _patternToken = 0;

  constructor(private state: XRSessionState) {}

  /**
   * Send a single haptic pulse.
   */
  pulse(hand: 'left' | 'right', intensity: number, duration: number): void {
    const actuator = this._getActuator(hand);
    if (!actuator) return;

    try {
      // pulse() REJECTS asynchronously when the controller disconnects mid-pulse
      // (or the UA refuses the effect) — the sync try/catch never saw it and it
      // surfaced as an unhandled rejection on every pulse against a gone pad.
      const result = actuator.pulse(intensity, duration) as unknown;
      if (result && typeof (result as Promise<unknown>).catch === 'function') {
        (result as Promise<unknown>).catch(_noop);
      }
    } catch {
      // Silently ignore — haptics may not be supported
    }
  }

  /**
   * Play a pattern of haptic pulses.
   * Returns a cancel function that stops remaining steps.
   */
  async playPattern(hand: 'left' | 'right', pattern: HapticPattern[]): Promise<void> {
    const token = ++this._patternToken;
    for (const step of pattern) {
      if (token !== this._patternToken) return; // cancelled by a newer call
      this.pulse(hand, step.intensity, step.duration);
      await this._wait(step.duration + step.pause);
    }
  }

  /**
   * Cancel any in-progress pattern playback.
   */
  cancelPattern(): void {
    this._patternToken++;
  }

  /**
   * Release in-flight pattern state. Callers (e.g. XRModule on session end)
   * MUST invoke this so pattern timers scheduled against a defunct session
   * cannot pulse into a later session's controllers when the state object
   * is reused.
   */
  dispose(): void {
    this._patternToken++;
  }

  /**
   * Check if haptics are available for a hand.
   */
  isAvailable(hand: 'left' | 'right'): boolean {
    return this._getActuator(hand) !== null;
  }

  private _getActuator(hand: 'left' | 'right'): GamepadHapticActuator | null {
    if (!this.state.isPresenting || !this.state.session) return null;

    const sources = this.state.session.inputSources;
    for (const source of sources) {
      if (source.handedness === hand && source.gamepad) {
        const actuators = (source.gamepad as any).hapticActuators;
        if (actuators && actuators.length > 0) {
          return actuators[0];
        }
        // Fallback: vibrationActuator (Chrome)
        const vibration = (source.gamepad as any).vibrationActuator;
        if (vibration) return vibration;
      }
    }
    return null;
  }

  private _wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
